import React, { useCallback, useEffect } from "react"
import { useLocation, Switch } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"

import { IRedux, IRoute } from "./services/types"
import { setEvent } from "./redux/event"
import { setLoading } from "./redux/loading"
import ROUTES from "./services/routes"
import { setSettings } from "./redux/settings"

import Login from "./screens/login"
import PrivateRoute from "./shared/privateRoute"
import PublicRoute from "./shared/publicRoute"
import PasswordForgot from "./screens/passwordForgot"
import CreateAccount from "./screens/createAccount"
import PasswordNew from "./screens/newPassword"
import Inscription from "./screens/inscription/inscription"
import Message from "./screens/message"
import UsersPresent from "./screens/usersPresent"
import Scan from "./screens/scan"
import EventApi from "./services/api/Event.api"
import SettingApi from "./services/api/Setting.api"
import Badge from "./screens/badge/badge"
import Home from "./screens/home"
import UsersRegistered from "./screens/users/users.registered"
import Photos from "./screens/photos/photos"

const routes: IRoute[] = [
  {
    path: ROUTES.LOGIN,
    component: Login
  },
  {
    path: ROUTES.PASSWORD_FORGOT,
    component: PasswordForgot
  },
  {
    path: ROUTES.PASSWORD_NEW,
    component: PasswordNew,
    isPrivate: true
  },
  {
    path: ROUTES.CREATE_ACCOUNT,
    component: CreateAccount
  },
  {
    path: ROUTES.HOME,
    component: UsersRegistered,
    isPrivate: true
  },
  {
    path: ROUTES.EVENTS_INSCRIPTION,
    component: Inscription,
    isPrivate: true
  },
  {
    path: ROUTES.MESSAGE,
    component: Message,
    isPrivate: true
  },
  {
    path: ROUTES.USERS_REGISTERED,
    component: UsersRegistered,
    isPrivate: true
  },
  {
    path: ROUTES.USERS_PRESENT,
    component: UsersPresent,
    isPrivate: true
  },
  {
    path: ROUTES.SCAN,
    component: Scan,
    isPrivate: true
  },
  {
    path: ROUTES.BADGE_DOWNLOAD,
    component: Badge,
    isPrivate: true
  },
  {
    path: ROUTES.PHOTOS,
    component: Photos,
    isPrivate: true
  }
]

const AppRouter = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const event = useSelector((state: IRedux) => state.event)

  const fetchEvent = useCallback(
    async (domainId: string | null, eventId: string | null) => {
      if (!domainId || !eventId) return
      dispatch(setLoading(true))
      try {
        const eventFetched = await new EventApi(Number(domainId)).getById(
          Number(eventId)
        )
        dispatch(setEvent(eventFetched))
        if (eventFetched) dispatch(setSettings(await new SettingApi().getOne()))
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch]
  )

  const setToken = useCallback(
    (token: string | null, domainId: string | null, eventId: string | null) => {
      if (!token) return
      localStorage.setItem("token", token)
      window.location.href = `${window.location.origin}${
        window.location.pathname
      }${eventId ? `?domain=${domainId}&event=${eventId}` : ""}`
    },
    []
  )

  useEffect(() => {
    const search = new URLSearchParams(location.search)
    const eventId = search.get("event")
    const domainId = search.get("domain")
    fetchEvent(domainId, eventId)
    setToken(search.get("token"), domainId, eventId)
  }, [fetchEvent, location.search, setToken])

  if (!event)
    return (
      <div className="has-text-centered">
        Pour accéder sur cette page, vous devez être dans le context d'un
        événement
      </div>
    )

  return (
    <Switch>
      {routes.map(({ path, isPrivate, component }: IRoute) =>
        isPrivate ? (
          <PrivateRoute key={path} path={path} component={component} exact />
        ) : (
          <PublicRoute key={path} path={path} component={component} exact />
        )
      )}
    </Switch>
  )
}

export default AppRouter
